import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import { getHome, pinTargetOf, setChartPinSize, setFavoriteOrder, setPin, withPinned } from '../services/favorites';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { normalizeId } from '../utils/ids';
import { orderByFavorites } from '../utils/collections';
import { isTokenValid } from '../utils';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

const SECTION_KEYS = ['pinned', 'charts', 'collections'];

// Which Home section a pin of this kind lives in (keepr-api favoriteUtils
// SECTIONS): a chart its own; every other pin — launchers of every kind,
// records, tags, combined lists — the ONE Pinned strip (KPR-1123).
const sectionOfPin = pin => (pin?.targetType === 'chartHome' ? 'charts' : 'pinned');

// A Home read with one section replaced. Pinned goes through withPinned, so
// the per-kind arrays derived from it (the rail's pinned tags, the palette)
// move with it.
const withSection = (home, section, list) => (section === 'pinned' ? withPinned(home, list) : { ...home, [section]: list });

/**
 * The signed-in Home page's data: GET /api/home — the caller's pins resolved
 * and access-filtered — plus the two things the page does to it.
 *
 *   busy      a request is in flight (loading is only "nothing has answered")
 *   home      { pinned, launchers, records, tags, combinedLists, charts,
 *               collections, collectionGroups, favoriteCards, personal,
 *               dropped } | null while loading |
 *               false on an API that has no Home yet (the page then shows
 *               Recent alone). `tags` are pinned tag pages and
 *               `collectionGroups` the person's private tags on collections
 *               (tags T5).
 *               `pinned` is the ONE Pinned strip (KPR-1123); the four
 *               per-kind arrays are derived from it (services/favorites
 *               withPinned) and change only with it.
 *   reorder(section, ids)   the section's new order ('pinned' | 'charts' |
 *                           'collections'), by pin ROW id; optimistic,
 *                           rolled back with a message on refusal
 *   unpin(pin)              optimistic, the same
 *   resize(pin, size)       a chart pin small or wide (charts A7, ruled
 *                           d18); optimistic, the same
 *   collections             the favorite collections, in the ONE
 *                           favorites order (FavoritesContext) — the rail's
 *                           and the collections page's, so the three never
 *                           disagree. DERIVED from the favorites once they
 *                           have loaded: an unstar anywhere leaves the strip
 *                           at once and a star joins it (drawn from the record
 *                           it was starred from), with no second Home read. A
 *                           favorite the Home read left out (no longer
 *                           readable) stays out; until the favorites answer,
 *                           it is home.collections in that order.
 *   reorderCollections(collectionIds)
 *                           saves that order through FavoritesContext, so
 *                           a reorder here moves the rail and the
 *                           collections page too (without the provider — a
 *                           test host — the section's own rows by row id)
 *
 * It re-reads whenever a pin changes anywhere (FavoritesContext bumps
 * `pinsVersion` — the item ⋯ menu, a quick add's menu, a launch row's pin),
 * so coming back to Home after pinning shows the pin. `enabled: false`
 * defers the fetch (the command palette reads Home only once it is open).
 *
 * Per account: a sign-out or another account signing in clears it at once
 * and drops any answer still in flight. The shell's palette holds this hook
 * through the /logout hop, so without that the next person at the keyboard
 * was offered the last one's pinned launchers by name.
 */
export default function useHome({ enabled = true } = {}) {
	const { message } = App.useApp();
	const favorites = useOptionalFavorites();
	const pinsVersion = favorites?.pinsVersion || 0;
	const [home, setHome] = useState(null);
	const [error, setError] = useState(null);
	// A request is on its way — the first, a re-read after a pin, or a Retry.
	// `loading` says only that nothing has answered yet; a Retry after a
	// failure needs this to read as a new wait rather than a stale error.
	const [busy, setBusy] = useState(false);
	// A snapshot the rollback reads — the state updater alone could restore a
	// value a newer optimistic change already replaced.
	const homeRef = useRef(null);
	homeRef.current = home;
	const requestRef = useRef(0);

	const session = useOnSessionChange(() => {
		requestRef.current += 1; // whatever is in flight answers for the last account
		setHome(null);
		setError(null);
		setBusy(false);
	});

	const load = useCallback(() => {
		if (!enabled || !isTokenValid()) return;
		const token = ++requestRef.current;
		const startedFor = getSessionUserId();
		setBusy(true);
		getHome()
			.then(data => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				setHome(data === null ? false : data);
				setError(null);
				setBusy(false);
			})
			.catch(err => {
				if (token !== requestRef.current || getSessionUserId() !== startedFor) return;
				setError(err);
				setBusy(false);
				setHome(current => current || false);
			});
	}, [enabled]);

	useEffect(load, [load, pinsVersion, session]);

	const reorder = useCallback(async (section, orderedIds) => {
		if (!SECTION_KEYS.includes(section)) return;
		const before = homeRef.current;
		if (!before) return;
		const byId = new Map((before[section] || []).map(pin => [normalizeId(pin._id), pin]));
		const next = orderedIds.map(id => byId.get(normalizeId(id))).filter(Boolean);
		setHome(withSection(before, section, next));
		try {
			await setFavoriteOrder(section, next.map(pin => normalizeId(pin._id)));
			// The rail draws the same collection order from the favorites cache.
			if (section === 'collections') favorites?.ensureLoaded?.({ force: true });
		} catch (err) {
			setHome(homeRef.current === null ? before : withSection(homeRef.current, section, before[section]));
			message.error('Could not save that order. Please try again.');
		}
	}, [favorites, message]);

	const unpin = useCallback(async (pin) => {
		const before = homeRef.current;
		if (!before || !pin) return;
		const section = sectionOfPin(pin);
		const rowId = normalizeId(pin._id);
		setHome(withSection(before, section, (before[section] || []).filter(entry => normalizeId(entry._id) !== rowId)));
		try {
			await setPin(pin.targetType, pinTargetOf(pin), false);
			// Every other surface's "Pinned" state follows (and this hook
			// re-reads, which changes nothing it has not already shown).
			favorites?.notePinsChanged?.();
		} catch (err) {
			setHome(current => (current ? withSection(current, section, before[section]) : before));
			message.error('Could not unpin that. Please try again.');
		}
	}, [favorites, message]);

	const resize = useCallback(async (pin, size) => {
		const before = homeRef.current;
		if (!before || !pin) return;
		const rowId = normalizeId(pin._id);
		const sized = list => (list || []).map(entry => (normalizeId(entry._id) === rowId ? { ...entry, size } : entry));
		setHome({ ...before, charts: sized(before.charts) });
		try {
			await setChartPinSize({ targetId: normalizeId(pin.target_id), collectionId: normalizeId(pin.collection_id) }, size);
		} catch (err) {
			setHome(current => (current ? { ...current, charts: before.charts } : before));
			message.error('Could not change that chart\'s size. Please try again.');
		}
	}, [message]);

	const favoriteOrder = favorites?.favoriteCollectionOrder;
	const favoritesLoaded = Boolean(favorites?.favoriteCollectionsLoaded);
	const favoriteRecords = favorites?.favoriteCollectionRecords;
	const collections = useMemo(() => {
		const rows = (home && home.collections) || [];
		if (!home || !favoritesLoaded || !Array.isArray(favoriteOrder)) {
			return orderByFavorites(rows, favoriteOrder, pin => pin?.target_id);
		}
		// The favorites are the list; the Home read is where a row's identity
		// (and its readability) comes from. A star made on this tab since the
		// read has no row there — its record stands in, shaped as a pin.
		const personalId = normalizeId(home.personal?._id);
		const byTarget = new Map(rows.map(pin => [normalizeId(pin?.target_id), pin]));
		return favoriteOrder
			.filter(id => id && id !== personalId)
			.map(id => {
				if (byTarget.has(id)) return byTarget.get(id);
				const record = favoriteRecords?.get?.(id);
				return record ? { _id: null, targetType: 'collection', target_id: id, collection_id: null, collection: record } : null;
			})
			.filter(Boolean);
	}, [home, favoritesLoaded, favoriteOrder, favoriteRecords]);

	const reorderCollections = useCallback((collectionIds) => {
		if (typeof favorites?.reorderCollections === 'function') return favorites.reorderCollections(collectionIds);
		const rowOf = new Map((homeRef.current?.collections || []).map(pin => [normalizeId(pin.target_id), normalizeId(pin._id)]));
		return reorder('collections', (collectionIds || []).map(id => rowOf.get(normalizeId(id))).filter(Boolean));
	}, [favorites, reorder]);

	return { home, loading: home === null, busy, error, reload: load, reorder, unpin, resize, collections, reorderCollections };
}
